import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import whiteVoteIcon from "../../../../assets/customerAssets/icons/whiteVoteIcon.svg";
import { useSelector } from "react-redux";

// ─── Count-up hook ─────────────────────────────────────────────────────────
function useCountUp(target, duration = 1200, delay = 0) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let rafId, startTime = null;
    const tid = setTimeout(() => {
      const step = (ts) => {
        if (!startTime) startTime = ts;
        const p = Math.min((ts - startTime) / duration, 1);
        setValue(Math.floor(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) rafId = requestAnimationFrame(step);
        else setValue(target);
      };
      rafId = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(tid); if (rafId) cancelAnimationFrame(rafId); };
  }, [target, duration, delay]);
  return value;
}

// ─── Particle spawner ───────────────────────────────────────────────────────
function spawnParticles({ canvas, containerRef, count = 150, originX, originY, speed = [3.5, 10], decay = [0.006, 0.016] }) {
  const parent = containerRef?.current;
  const W = parent ? parent.offsetWidth  : 700;
  const H = parent ? parent.offsetHeight : 260;
  canvas.width  = W;
  canvas.height = H;
  const cx = originX ?? W / 2;
  const cy = originY ?? H / 2;
  const COLORS = ["#FFD700","#FFF176","#FFFFFF","#FFB300","#FFFDE7","#FF8C00","#FFF9C4","#FFEE58","#FFC107","#FFE082","#F9A825","#FFCA28"];
  return Array.from({ length: count }, () => {
    const angle = Math.random() * Math.PI * 2;
    const spd   = speed[0] + Math.random() * (speed[1] - speed[0]);
    const sz    = 2 + Math.random() * 5;
    const sr    = Math.random();
    return {
      x: cx, y: cy,
      vx: Math.cos(angle) * spd,
      vy: Math.sin(angle) * spd - Math.random() * 2,
      size: sz, color: COLORS[Math.floor(Math.random() * COLORS.length)],
      alpha: 1,
      decay: decay[0] + Math.random() * (decay[1] - decay[0]),
      shape: sr < 0.4 ? "circle" : sr < 0.75 ? "square" : "diamond",
      rotate: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.22,
      twinkle: Math.random() > 0.55,
      twinkleSpeed: 0.05 + Math.random() * 0.12,
      twinklePhase: Math.random() * Math.PI * 2,
    };
  });
}

// ─── Canvas particle component ──────────────────────────────────────────────
const BannerParticles = ({ trigger, containerRef, hovered }) => {
  const canvasRef = useRef(null);
  const stateRef  = useRef({ particles: [], rafId: null, running: false, hovering: false });
  const frameRef  = useRef(0);

  const startLoop = useCallback(() => {
    const s = stateRef.current;
    if (s.running) return;
    s.running = true;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const loop = () => {
      const W = canvas.width, H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      frameRef.current++;
      s.particles = s.particles.filter(p => p.alpha > 0);
      s.particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy;
        p.vy += 0.09; p.vx *= 0.983; p.vy *= 0.983;
        p.alpha -= p.decay; p.rotate += p.rotSpeed;
        const tw = p.twinkle ? 0.65 + 0.35 * Math.sin(frameRef.current * p.twinkleSpeed + p.twinklePhase) : 1;
        ctx.save();
        ctx.globalAlpha = Math.max(p.alpha * tw, 0);
        ctx.fillStyle = p.color; ctx.shadowColor = p.color; ctx.shadowBlur = 5;
        ctx.translate(p.x, p.y); ctx.rotate(p.rotate);
        if (p.shape === "circle") { ctx.beginPath(); ctx.arc(0, 0, p.size, 0, Math.PI * 2); ctx.fill(); }
        else if (p.shape === "square") { ctx.fillRect(-p.size/2, -p.size/2, p.size, p.size); }
        else { ctx.beginPath(); ctx.moveTo(0,-p.size); ctx.lineTo(p.size*0.6,0); ctx.lineTo(0,p.size); ctx.lineTo(-p.size*0.6,0); ctx.closePath(); ctx.fill(); }
        ctx.restore();
      });
      if (s.particles.length > 0 || s.hovering) { s.rafId = requestAnimationFrame(loop); }
      else { s.running = false; ctx.clearRect(0, 0, W, H); }
    };
    s.rafId = requestAnimationFrame(loop);
  }, []);

  useEffect(() => {
    if (!trigger || !canvasRef.current) return;
    const newP = spawnParticles({ canvas: canvasRef.current, containerRef, count: 150 });
    stateRef.current.particles.push(...newP);
    startLoop();
  }, [trigger]);

  useEffect(() => {
    const s = stateRef.current;
    s.hovering = hovered;
    if (!hovered) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const interval = setInterval(() => {
      const W = canvas.width  || (containerRef?.current?.offsetWidth  ?? 700);
      const H = canvas.height || (containerRef?.current?.offsetHeight ?? 260);
      const newP = spawnParticles({
        canvas, containerRef, count: 18,
        originX: W * 0.1 + Math.random() * W * 0.8,
        originY: H * 0.1 + Math.random() * H * 0.55,
        speed: [1.5, 5], decay: [0.012, 0.022],
      });
      stateRef.current.particles.push(...newP);
      startLoop();
    }, 80);
    return () => { clearInterval(interval); s.hovering = false; };
  }, [hovered, startLoop]);

  useEffect(() => () => { const s = stateRef.current; if (s.rafId) cancelAnimationFrame(s.rafId); }, []);

  return (
    <canvas ref={canvasRef} style={{ position:"absolute", top:0, left:0, width:"100%", height:"100%", pointerEvents:"none", zIndex:30 }} />
  );
};

// ─── Sonar rings ─────────────────────────────────────────────────────────────
const SonarRings = ({ visible }) => {
  const rings = [
    { s:110, bw:2.5, color:"rgba(255,215,0,0.95)", dur:2.0, delay:0,   blur:0, scale:2.2 },
    { s:110, bw:1.5, color:"rgba(255,215,0,0.55)", dur:2.0, delay:0.5, blur:2, scale:2.2 },
    { s:110, bw:1.5, color:"rgba(255,200,50,0.45)", dur:2.8, delay:0.2, blur:1, scale:3.2 },
    { s:110, bw:1,   color:"rgba(255,200,50,0.25)", dur:2.8, delay:0.9, blur:3, scale:3.2 },
    { s:110, bw:1,   color:"rgba(255,255,180,0.30)", dur:3.6, delay:0.7, blur:4, scale:4.2 },
    { s:110, bw:0.5, color:"rgba(255,255,255,0.15)", dur:3.6, delay:1.5, blur:6, scale:4.2 },
  ];
  return (
    <>
      {rings.map((r, i) => (
        <Box key={i} sx={{
          position:"absolute", top:"50%", left:"50%",
          width:`${r.s}px`, height:`${r.s}px`,
          mt:`-${r.s/2}px`, ml:`-${r.s/2}px`,
          borderRadius:"50%",
          border:`${r.bw}px solid ${r.color}`,
          filter: r.blur ? `blur(${r.blur}px)` : "none",
          pointerEvents:"none",
          opacity: visible ? 1 : 0,
          zIndex: 5,
          animation: visible ? `sonarClub_${i} ${r.dur}s cubic-bezier(0.12,0.5,0.3,1) ${r.delay}s infinite` : "none",
          [`@keyframes sonarClub_${i}`]: {
            "0%":   { transform:"scale(1)",         opacity: i%2===0 ? 0.95 : 0.6 },
            "60%":  { opacity: i%2===0 ? 0.2 : 0.1 },
            "100%": { transform:`scale(${r.scale})`, opacity:0 },
          },
        }} />
      ))}
      {visible && (
        <Box sx={{
          position:"absolute", top:"50%", left:"50%",
          width:"130px", height:"130px", mt:"-65px", ml:"-65px",
          borderRadius:"50%",
          background:"radial-gradient(circle, rgba(255,215,0,0.18) 0%, rgba(255,215,0,0.06) 50%, transparent 70%)",
          pointerEvents:"none", zIndex:4,
          animation:"haloBreathClub 3s ease-in-out infinite",
          "@keyframes haloBreathClub": {
            "0%,100%": { transform:"scale(1)",    opacity:0.8 },
            "50%":     { transform:"scale(1.18)", opacity:1   },
          },
        }} />
      )}
    </>
  );
};

// 2nd → 3rd → 1st entry order
const ANIM_DELAY = [750, 200, 480];

const Position = ({ index, leaderboard, club_logo, bannerRef, bannerHovered }) => {
  const user    = useSelector((state) => state.user);
  const [visible, setVisible] = useState(false);
  const [burst,   setBurst]   = useState(false);
  const isFirst = index === 0;
  const delay   = ANIM_DELAY[index];

  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(true);
      if (isFirst) setTimeout(() => setBurst(true), 420);
    }, delay);
    return () => clearTimeout(t);
  }, [delay, isFirst]);

  const animatedVotes = useCountUp(leaderboard[index]?.voteCount || 0, 1200, delay + 300);

  return (
    <Box
      className={index === 0 ? "position-main first" : index === 1 ? "position-main second" : "position-main third"}
      sx={{ overflow:"visible !important", position:"relative" }}
    >
      {/* Sonar rings outside img-wrapper */}
      {isFirst && <SonarRings visible={visible} />}

      <Box sx={{ position:"relative" }} className="content-wrapper">

        {/* Crown for 1st place */}
        {isFirst && (
          <Box sx={{
            position:"absolute", top:"-38px", left:"50%",
            transform: visible
              ? "translateX(-50%) translateY(0) scale(1)"
              : "translateX(-50%) translateY(-20px) scale(0.4)",
            opacity: visible ? 1 : 0,
            transition:"opacity 0.5s ease, transform 0.6s cubic-bezier(0.34,1.5,0.64,1)",
            transitionDelay:"600ms",
            fontSize:"36px", lineHeight:1, zIndex:10,
            filter:"drop-shadow(0 2px 8px rgba(255,200,0,0.85)) drop-shadow(0 0 16px rgba(255,180,0,0.5))",
            animation: visible ? "crownBobClub 3s ease-in-out 1.3s infinite" : "none",
            "@keyframes crownBobClub": {
              "0%,100%": { transform:"translateX(-50%) translateY(0px) rotate(-3deg)"  },
              "50%":     { transform:"translateX(-50%) translateY(-5px) rotate(3deg)"  },
            },
          }}>👑</Box>
        )}

        {/* Avatar zoom-in */}
        <Box
          className="img-wrapper"
          sx={{
            position:"relative",
            opacity:   visible ? 1 : 0,
            transform: visible ? "scale(1)" : "scale(0.1)",
            transition:"opacity 0.5s ease, transform 0.7s cubic-bezier(0.34,1.45,0.64,1)",
            zIndex:6,
          }}
        >
          {/* uses club_logo exactly as original */}
          <img
            src={user.data?.profile_img || club_logo}
            style={{ width:"100%", height:"100%" }}
            alt=""
          />
        </Box>

        {/* Text content */}
        <Box
          className="content"
          sx={{
            opacity:   visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            transition:"opacity 0.4s ease, transform 0.4s ease",
            transitionDelay:"240ms",
            position:"relative", zIndex:6,
          }}
        >
          <Box className="floating-content view-club">
            <Typography component={"span"}>
              {index === 0 ? "1" : index === 1 ? "2" : "3"}
            </Typography>
          </Box>
          <Typography component={"span"} sx={{ marginTop:"10px" }}>
            {leaderboard[index].goal_scorer}
          </Typography>
          <Box sx={{ display:"flex", alignItems:"start", justifyContent:"center", gap:"2px" }}>
            <img src={whiteVoteIcon} height={20} width={20} alt="" />
            {/* animated vote count */}
            <Typography component={"span"}>{animatedVotes} Votes</Typography>
          </Box>
        </Box>
      </Box>

      {/* Particles (burst + hover drizzle) */}
      {isFirst && (
        <BannerParticles trigger={burst} containerRef={bannerRef} hovered={bannerHovered} />
      )}
    </Box>
  );
};

export default Position;